import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Header from '../components/Header';
import { Footer } from '../components/Footer';
import { useToast } from 'components/toast/ToastProvider';
import { useAuthStatus } from 'hooks/useAuthStatus';
import { useSubscriptionAccess } from 'hooks/useSubscriptionAccess';
import { PATHS } from 'routes/paths';
import { subscriptionService } from 'services/subscription/subscriptionService';
import { getErrorMessage } from 'utils/http';
import { authStorage } from 'utils/storage';

const plans = [
  {
    id: 'basic',
    title: 'Basic',
    priceLabel: '29',
    purchasePrice: 299,
    purchasePlanName: 'Basic Monthly',
    features: ['Access to Basic Courses', 'Access on any device', 'Cancel Anytime'],
    highlighted: false,
  },
  {
    id: 'premium',
    title: 'Premium',
    priceLabel: '99',
    purchasePrice: 499,
    purchasePlanName: 'Pro Monthly',
    features: ['Access to All Courses', 'Access On Any Device', 'Unlimited Downloads', 'Unlimited Transcripts', 'Cancel Anytime'],
    highlighted: true,
  },
  {
    id: 'standard',
    title: 'Standard',
    priceLabel: '59',
    purchasePrice: 399,
    purchasePlanName: 'Standard Monthly',
    features: ['Access to Advanced Courses', 'Access on any device', 'Limited Downloads', 'Limited Transcripts', 'Cancel Anytime'],
    highlighted: false,
  },
] as const;

export default function PricingPage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();
  const hasSubscriptionAccess = useSubscriptionAccess();
  const { showError, showSuccess } = useToast();
  const [isPurchasingPlan, setIsPurchasingPlan] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && hasSubscriptionAccess) {
      navigate(PATHS.library, { replace: true });
    }
  }, [hasSubscriptionAccess, isAuthenticated, navigate]);

  const stripedBg = {
    backgroundImage: 'repeating-linear-gradient(-45deg, var(--bg-stripe), var(--bg-stripe) 2px, var(--bg-body) 2px, var(--bg-body) 10px)',
    backgroundColor: 'var(--bg-body)'
  };

  type CheckmarkProps = { highlighted?: boolean };
  const Checkmark: React.FC<CheckmarkProps> = ({ highlighted = false }) => (
    <svg
      className={`w-5 h-5 flex-shrink-0 ${highlighted ? 'text-[#25D366]' : 'text-[var(--text-900)]'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
    </svg>
  );

  const handleGetStarted = async (plan: (typeof plans)[number]) => {
    if (!isAuthenticated) {
      navigate(PATHS.signup);
      return;
    }

    setIsPurchasingPlan(plan.id);

    try {
      const response = await subscriptionService.purchase({
        plan_name: plan.purchasePlanName,
        price: plan.purchasePrice,
      });

      authStorage.setSubscriptionActive(true);
      showSuccess(response.message || 'Plan activated successfully');
      navigate(PATHS.library);
    } catch (error) {
      showError(getErrorMessage(error, 'Unable to complete plan purchase.'));
    } finally {
      setIsPurchasingPlan(null);
    }
  };

  return (
    <div style={stripedBg} className="min-h-screen w-full py-2 px-2 sm:py-8 sm:px-4 md:px-8 lg:px-12 flex justify-center font-sans text-[var(--text-900)] overflow-hidden">
      <div
        className="w-full bg-[var(--bg-box)] rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden border border-[var(--border-200)] flex flex-col relative"
        style={{ boxShadow: 'var(--shadow-box)' }}
      >
        <Header />

        <main className="flex-grow pb-20 sm:pb-28 md:pb-32">
          <section className="pt-20 sm:pt-28 md:pt-32 pb-10 sm:pb-16 md:pb-20 px-2 sm:px-4 md:px-8 text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-[var(--text-900)] mb-6 tracking-tight">
              Simple, transparent pricing
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-[var(--text-500)] leading-relaxed font-medium">
              Choose the plan that best fits your learning goals. Upgrade, downgrade, or cancel anytime.
            </p>
          </section>

          <section className="px-2 sm:px-4 md:px-8 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
              {plans.map((plan) => {
                const isBusy = isPurchasingPlan === plan.id;

                return (
                  <div
                    key={plan.id}
                    className={plan.highlighted ? 'bg-[var(--primary)] rounded-[2rem] p-10 flex flex-col h-full md:scale-105 relative z-10' : 'bg-[var(--bg-white)] border border-[var(--border-200)] rounded-[2rem] p-10 flex flex-col h-full'}
                    style={{ boxShadow: plan.highlighted ? 'var(--shadow-box)' : 'var(--shadow-sm)' }}
                  >
                    {plan.highlighted ? (
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-[#25D366] to-transparent opacity-50 rounded-full"></div>
                    ) : null}

                    <div className={`text-center ${plan.highlighted ? 'border-b border-[var(--text-700)]' : 'border-b border-[var(--border-100)]'} pb-8 mb-8`}>
                      <h2 className={`text-2xl font-bold mb-4 ${plan.highlighted ? 'text-white' : 'text-[var(--text-900)]'}`}>{plan.title}</h2>
                      <div className={`flex items-baseline justify-center gap-1 ${plan.highlighted ? 'text-white' : ''}`}>
                        <span className={`font-extrabold tracking-tight ${plan.highlighted ? 'text-6xl' : 'text-5xl text-[var(--text-900)]'}`}>{plan.priceLabel}</span>
                        <span className={`font-medium ${plan.highlighted ? 'text-white' : 'text-[var(--text-500)]'}`}>/Per Month</span>
                      </div>
                    </div>

                    <ul className="space-y-6 mb-10 flex-grow">
                      {plan.features.map((feature) => (
                        <li key={feature} className={`flex items-center gap-4 font-medium ${plan.highlighted ? 'text-white' : 'text-[var(--text-600)]'}`}>
                          <Checkmark highlighted={plan.highlighted} /> {feature}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => void handleGetStarted(plan)}
                      disabled={isBusy}
                      className={plan.highlighted ? 'bg-[var(--bg-white)] text-[var(--text-900)] px-12 py-5 rounded-2xl font-bold text-lg hover:bg-[var(--surface-100)] transition-all hover:-translate-y-1 shadow-xl relative z-10 disabled:opacity-70' : 'w-full bg-[var(--surface-100)] text-[var(--text-900)] border border-[var(--border-200)] px-6 py-4 rounded-xl font-bold hover:bg-[var(--surface-200)] transition-colors mt-auto disabled:opacity-70'}
                    >
                      {isBusy ? 'PROCESSING...' : 'GET STARTED'}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </div>
  );
}
